import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';

// =============================================================================
// Alert Detail Types
// =============================================================================

export interface ReorderAlertItem {
  id: string;
  item: string;
  entity_identifier: string | null;
  nsn: string | null;
  lin: string | null;
  current_qty: number;
  threshold: number;
  shortage: number;
}

export interface ApproachingReorderItem {
  id: string;
  item: string;
  entity_identifier: string | null;
  nsn: string | null;
  lin: string | null;
  current_qty: number;
  threshold: number;
  qty_to_target: number;
}

export interface OverdueThrItem {
  id: string;
  individual: string | null;
  individual_id: string | null;
  asset: string | null;
  asset_identifier: string | null;
  due_date: string;
  days_overdue: number;
}

export interface OpenDocumentItem {
  id: string;
  display_name: string | null;
  document_type: string | null;
  individual: string | null;
  individual_id: string | null;
  comments: string | null;
  created_at: string;
  asset_count: number;
}

export interface OpenOrderItem {
  id: string;
  order_number: string | null;
  display_name: string | null;
  log_form_number: string | null;
  description: string | null;
  order_date: string | null;
  vendor: string | null;
  line_item_count: number;
  total_ordered: number;
  total_received: number;
  order_status: string;
}

export interface MissingLocationAlertItem {
  id: string;
  entity_type: 'asset' | 'catalog';
  display_name: string;
  entity_identifier: string | null;
  lin: string | null;
  nsn: string | null;
  catalog_id?: string | null;
  missing_field: 'location_id' | 'primary_location_id';
}

export interface MissingLocationAlertSummary {
  total_count: number;
  asset_count: number;
  catalog_count: number;
}

type EntityLocationRow = {
  id: string;
  entity_type: 'asset' | 'catalog';
  display_name: string | null;
  entity_identifier: string | null;
  entity_data: Record<string, unknown> | null;
};

const isMissingLocationField = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }

  return typeof value === 'string' && value.trim().length === 0;
};

const toMissingLocationItem = (row: EntityLocationRow): MissingLocationAlertItem => {
  const data = (row.entity_data ?? {}) as Record<string, unknown>;
  return {
    id: row.id,
    entity_type: row.entity_type,
    display_name: row.display_name ?? 'Unknown item',
    entity_identifier: row.entity_identifier ?? null,
    lin: typeof data.lin === 'string' ? data.lin : null,
    nsn: typeof data.nsn === 'string' ? data.nsn : null,
    catalog_id: typeof data.catalog_id === 'string' ? data.catalog_id : null,
    missing_field: row.entity_type === 'asset' ? 'location_id' : 'primary_location_id',
  };
};

const sortMissingLocationItems = (
  items: MissingLocationAlertItem[],
): MissingLocationAlertItem[] => {
  return items.sort((a, b) => {
    if (a.entity_type !== b.entity_type) {
      return a.entity_type.localeCompare(b.entity_type);
    }
    return a.display_name.localeCompare(b.display_name, undefined, { sensitivity: 'base' });
  });
};

const fetchMissingLocationItemsFromEntities = async (): Promise<MissingLocationAlertItem[]> => {
  const catalogRows = await httpClient.getAll<EntityLocationRow>(
    '/api/rest/data/v1/entities?entity_type=eq.catalog&is_current=eq.true&deleted_at=is.null&status=eq.active&select=id,entity_type,display_name,entity_identifier,entity_data',
  );

  const missingCatalogs = catalogRows.filter((row) => {
    const data = (row.entity_data ?? {}) as Record<string, unknown>;
    return isMissingLocationField(data.primary_location_id);
  });

  return sortMissingLocationItems(missingCatalogs.map(toMissingLocationItem));
};

const fetchMissingLocationItems = async (): Promise<MissingLocationAlertItem[]> => {
  try {
    const items = await httpClient.get<MissingLocationAlertItem[]>(
      '/api/rest/data/v1/missing_location_alert_items?entity_type=eq.catalog&order=display_name.asc',
    );
    return sortMissingLocationItems(items);
  } catch {
    return fetchMissingLocationItemsFromEntities();
  }
};

// =============================================================================
// Hooks
// =============================================================================

/**
 * Fetches catalog items at or below reorder threshold
 */
export const useReorderAlertItems = (enabled = false) => {
  return useQuery({
    queryKey: ['dashboard-alert', 'reorder-items'],
    queryFn: async (): Promise<ReorderAlertItem[]> => {
      return httpClient.get<ReorderAlertItem[]>('/api/rest/data/v1/reorder_alert_items');
    },
    enabled,
    staleTime: 60 * 1000,
  });
};

/**
 * Fetches catalog items within 20% of reorder threshold
 */
export const useApproachingReorderItems = (enabled = false) => {
  return useQuery({
    queryKey: ['dashboard-alert', 'approaching-reorder-items'],
    queryFn: async (): Promise<ApproachingReorderItem[]> => {
      return httpClient.get<ApproachingReorderItem[]>(
        '/api/rest/data/v1/approaching_reorder_items',
      );
    },
    enabled,
    staleTime: 60 * 1000,
  });
};

/**
 * Fetches temporary hand receipts past their due date
 */
export const useOverdueThrItems = (enabled = false) => {
  return useQuery({
    queryKey: ['dashboard-alert', 'overdue-thr-items'],
    queryFn: async (): Promise<OverdueThrItem[]> => {
      return httpClient.get<OverdueThrItem[]>('/api/rest/data/v1/overdue_thr_items');
    },
    enabled,
    staleTime: 60 * 1000,
  });
};

/**
 * Fetches open document records (DD200, DA7923, etc.) in suspense status
 */
export const useOpenDocumentItems = (enabled = false) => {
  return useQuery({
    queryKey: ['dashboard-alert', 'open-document-items'],
    queryFn: async (): Promise<OpenDocumentItem[]> => {
      return httpClient.get<OpenDocumentItem[]>('/api/rest/data/v1/open_document_items');
    },
    enabled,
    staleTime: 60 * 1000,
  });
};

/**
 * Fetches open purchase requisitions not yet fully received
 */
export const useOpenOrderItems = (enabled = false) => {
  return useQuery({
    queryKey: ['dashboard-alert', 'open-order-items'],
    queryFn: async (): Promise<OpenOrderItem[]> => {
      return httpClient.get<OpenOrderItem[]>('/api/rest/data/v1/open_order_items');
    },
    enabled,
    staleTime: 60 * 1000,
  });
};

/**
 * Fetches count summary for active catalog items missing required location fields.
 */
export const useMissingLocationAlertSummary = (enabled = true) => {
  return useQuery({
    queryKey: ['dashboard-alert', 'missing-location-summary'],
    queryFn: async (): Promise<MissingLocationAlertSummary> => {
      try {
        const rows = await httpClient.get<MissingLocationAlertSummary[]>(
          '/api/rest/data/v1/missing_location_alert_summary',
        );

        const row = rows?.[0];
        if (!row) {
          return {
            total_count: 0,
            asset_count: 0,
            catalog_count: 0,
          };
        }

        return {
          total_count: row.catalog_count,
          asset_count: 0,
          catalog_count: row.catalog_count,
        };
      } catch {
        const items = await fetchMissingLocationItemsFromEntities();
        return {
          total_count: items.length,
          asset_count: 0,
          catalog_count: items.filter((item) => item.entity_type === 'catalog').length,
        };
      }
    },
    enabled,
    staleTime: 60 * 1000,
  });
};

/**
 * Fetches active catalog items missing required location fields.
 */
export const useMissingLocationAlertItems = (enabled = false) => {
  return useQuery({
    queryKey: ['dashboard-alert', 'missing-location-items'],
    queryFn: async (): Promise<MissingLocationAlertItem[]> => {
      return fetchMissingLocationItems();
    },
    enabled,
    staleTime: 60 * 1000,
  });
};
